import { CustomerDetailFragment, GuestAddressFragment, PersonIdentificationDocumentFragment } from 'src/api/api.generated'
import { compareDates, getDayDiff } from 'src/utils/date'
import { Customer } from '../api/update-reserved-resource-guests.types'
import { GuestAddress, GuestIdentificationDocument, GuestOccupancy } from '../types/guests.types'
import { ReservationF } from '../types/reservation-detail.types'

export function emptyGuestOccupancy(start?: Date, end?: Date): GuestOccupancy {
  return {
    type: null,
    cityTaxExoneration: null,
    start: start ?? null,
    end: end ?? null,
    guestId: null,
    firstName: null,
    language: null,
    lastName: null,
    gender: null,
    birthDate: null,
    birthAddress: null,
    address: null,
    nationalityId: null,
    identificationDocument: null,
    phase: null,
    checkin: null,
    checkout: null,
  }
}

export function fragmentToGuestAddress(f: GuestAddressFragment | null | undefined): GuestAddress | null {
  if (!f) return null
  const country = f.country
  if (!country) return null
  return {
    countryId: country.id,
    municipalityId: f.municipalityId ?? null,
    province: f.province ?? null,
    city: country.isoCode === 'IT' && f.municipality ? f.municipality.name : f.city ?? null,
  }
}

export function fragmentToIdentificationDocument(f: PersonIdentificationDocumentFragment | null | undefined): GuestIdentificationDocument | null {
  if (!f) return null
  return {
    documentId: f.documentId,
    number: f.number,
    expirationDate: f.expirationDate,
    countryId: f.countryId,
    city: f.city ?? null,
  }
}

export function fragmentToCustomer(f: CustomerDetailFragment): Customer {
  return {
    id: f.id,
    language: f.language,
    lastName: f.lastName ?? '',
    firstName: f.firstName ?? '',
    gender: f.person?.gender ?? null,
    birthDate: f.person?.birthDate ?? null,
    birthAddress: fragmentToGuestAddress(f.person?.birthAddress),
    address: fragmentToGuestAddress(f.address),
    nationalityId: f.person?.nationalityId ?? null,
    identificationDocument: f.person?.identificationDocument
      ? {
          documentId: f.person.identificationDocument.documentId,
          number: f.person.identificationDocument.number,
          expirationDate: f.person.identificationDocument.expirationDate,
          countryId: f.person.identificationDocument.countryId,
          city: f.person.identificationDocument.city ?? null,
        }
      : null,
  }
}

export function updateGuestOccupancyGuest(f: CustomerDetailFragment, v: GuestOccupancy): GuestOccupancy {
  const { id: guestId, ...c } = fragmentToCustomer(f)
  return {
    type: v.type,
    cityTaxExoneration: v.cityTaxExoneration,
    start: v.start,
    end: v.end,
    phase: v.phase,
    guestId,
    checkin: v.checkin,
    checkout: v.checkout,
    ...c,
  }
}

export function defaultGuestOccupancyStart(reservedResourceStart: Date, reservedResourceEnd: Date, hotelDate: Date): Date {
  return compareDates(reservedResourceStart, hotelDate) <= 0 && compareDates(hotelDate, reservedResourceEnd) < 0 && getDayDiff(reservedResourceStart, hotelDate) > 1 ? hotelDate : reservedResourceStart
}

export function isGroupMemberToConsiderAsSingleGuest(isGroup: boolean, g: GuestOccupancy, reservation: ReservationF) {
  return Boolean(isGroup && g.start && reservation.minStart && compareDates(g.start, reservation.minStart) !== 0)
}
